/**
 * React Query hooks for GHL Qualifier OAuth token health.
 *
 * Mirrors `useTokenHealth.ts` but points at the Qualifier app's endpoints.
 * The two health checks read different tables:
 *   - useTokenHealthMap()           → Middleware (ghl_oauth_tokens)
 *   - useQualifierTokenHealthMap()  → Qualifier (ghl_qualifier_oauth_tokens)
 *
 * Both hooks return the same `LocationTokenStatus` shape and reuse the
 * shared `TokenStatus` / `RevalidateError` types so presentational components
 * (`TokenStatusBadge`, the action buttons) stay provider-agnostic.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  RevalidateError,
  type LocationTokenStatus,
  type RevalidateResult,
  type TokenStatus,
} from './useTokenHealth'

const QUALIFIER_URL = import.meta.env.VITE_GHL_QUALIFIER_URL as string | undefined | null

/** How often to re-poll qualifier token health. 60s matches the Middleware hook. */
const TOKEN_HEALTH_POLL_INTERVAL_MS = 60 * 1000

// ---------------------------------------------------------------------------
// Types — re-exported from useTokenHealth so consumers can import either source
// ---------------------------------------------------------------------------

export type { TokenStatus, LocationTokenStatus, RevalidateResult }
export { RevalidateError }

export interface TokenHealthMap {
  readonly locations: Record<string, LocationTokenStatus>
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches per-location Qualifier OAuth token health.
 * Returns null if VITE_GHL_QUALIFIER_URL is not configured or the request fails —
 * the cards then render gray "unknown" badges and Revalidate/Reinstall stay
 * disabled, matching the Middleware degradation pattern.
 */
export function useQualifierTokenHealthMap(enabled = true) {
  return useQuery<TokenHealthMap | null>({
    queryKey: ['admin', 'ghl-qualifier-token-health'],
    queryFn: async () => {
      if (!QUALIFIER_URL) return null

      const response = await fetch(`${QUALIFIER_URL}/health/auth/locations`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      })

      if (!response.ok) return null

      const data = (await response.json()) as { status: string } & TokenHealthMap
      return { locations: data.locations ?? {} }
    },
    refetchInterval: TOKEN_HEALTH_POLL_INTERVAL_MS,
    staleTime: 30 * 1000,
    enabled: enabled && !!QUALIFIER_URL,
    retry: false,
  })
}

/**
 * Force-refresh a specific location's Qualifier OAuth token.
 * Invalidates the Qualifier token-health cache on success so the badge
 * updates immediately without waiting for the next 60s poll.
 */
export function useRevalidateQualifierToken() {
  const queryClient = useQueryClient()

  return useMutation<RevalidateResult, RevalidateError, string>({
    mutationFn: async (locationId: string) => {
      if (!QUALIFIER_URL) {
        throw new RevalidateError('Qualifier URL not configured', false)
      }

      const response = await fetch(
        `${QUALIFIER_URL}/health/auth/refresh/${encodeURIComponent(locationId)}`,
        {
          method: 'POST',
          signal: AbortSignal.timeout(15_000),
        }
      )

      const body = (await response.json()) as {
        success: boolean
        error?: string
        requiresReinstall?: boolean
        locationId: string
        status?: LocationTokenStatus['status']
        minutesUntilExpiry?: number
        expiresAt?: string
      }

      if (!response.ok) {
        throw new RevalidateError(
          body.error ?? `Refresh failed (${response.status})`,
          body.requiresReinstall ?? false
        )
      }

      return body as RevalidateResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ghl-qualifier-token-health'] })
    },
  })
}
